




















































import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { faSave } from '@fortawesome/free-solid-svg-icons'
import { CLASSIFIED_FORM_NS } from '~/store/modules/shared/classifieds/form/state'
import {
  SET_INVALID_TRIGGERED,
  SET_SHOW_FLOATING_SAVE_BUTTON
} from '~/store/modules/shared/classifieds/form/mutation-types'
import { APP_NS } from '~/store/modules/shared/app/state'
import { CategoryId } from '~/models/category/types'
import { Corner, FloatingCornerElementName } from '~/models/layout'
import { PAGE_NS } from '~/store/modules/shared/page/state'
import { nextTick } from '@nuxtjs/composition-api'
import { mapDeps } from '~/plugins/dependency-container/utils'
import UserAgentService from '~/services/UserAgentService'
import { USER_AGENT_NS } from '~/store/modules/shared/userAgent/state'

export default {
  components: {
    NewPaidClassifiedAlert: () => import('./NewPaidClassifiedAlert.vue'),
    CConfirmationModal: () =>
      import('~/components/shared/configurable/modal/CConfirmationModal.vue')
  },
  data() {
    return {
      termsAccepted: false,
      requiresPayment: false,
      isWarningConfirmationVisible: false,
      warningTitle: '',
      warningText: ''
    }
  },
  computed: {
    icons: () => ({
      save: faSave
    }),
    ...mapState(CLASSIFIED_FORM_NS, {
      formRequiresTerms: state => {
        return (
          state.genericFormFields !== null &&
          state.genericFormFields.accepted_terms &&
          state.genericFormFields.accepted_terms.required
        )
      },
      price: state =>
        state.fieldValues?.price?.price || state.fieldValues?.price,
      model: state => state.fieldValues?.makemodel?.model,
      months: state => state.fieldValues?.price?.months,
      editMode: state => state.editMode,
      loading: state => state.loading,
      categoryIds: state => state.categoryIds,
      pageKey: state => state.pageKey,
      invalidTriggered: state => state.invalidTriggered,
      showFloatingSaveButton: state => state.showFloatingSaveButton
    }),
    ...mapGetters(APP_NS, {
      isPlot: 'isPlot'
    }),
    ...mapGetters(CLASSIFIED_FORM_NS, {
      formChanged: 'formChanged'
    }),
    ...mapGetters(USER_AGENT_NS, {
      isPc: 'isPc'
    }),
    ...mapDeps({
      userAgentService: UserAgentService
    }),
    requiresTerms() {
      return !this.editMode && this.formRequiresTerms
    },
    termsPath() {
      if (this.isPlot) {
        return '/information/faq/terms/'
      }
      return '/information/terms/'
    },
    hasPrice() {
      let hasAnyMonthPrice = null
      if (this.months) {
        hasAnyMonthPrice = Object.values(this.months).some(m => !!m.price)
      }
      return this.price || hasAnyMonthPrice
    },
    isRental(): boolean {
      return this.categoryIds.includes(CategoryId.RENTALS)
    },
    isJobs(): boolean {
      return this.categoryIds.includes(CategoryId.JOBS)
    },
    isCar(): boolean {
      return this.categoryIds.includes(CategoryId.CARS)
    }
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },
  mounted() {
    this.setInvalidTriggered(false)
    if (this.editMode) {
      this.addCornerElement({
        name: FloatingCornerElementName.SAVE_BUTTON,
        corner: Corner.BOTTOM_RIGHT,
        componentInstance: this
      })
    }
    window.addEventListener('beforeunload', this.onBeforeUnload)

    const formElement = document.getElementById(`form-${this.pageKey}`)
    // checking for firefox here cause it already has a buggy behavior for error scrolling https://bugzilla.mozilla.org/show_bug.cgi?id=643758
    if (formElement && !this.userAgentService.isFirefox()) {
      formElement.addEventListener('invalid', this.onInvalid, true)
    }
  },
  methods: {
    ...mapActions(PAGE_NS, {
      addCornerElement: 'addCornerElement'
    }),
    ...mapActions(CLASSIFIED_FORM_NS, {
      fieldChanged: 'fieldChanged',
      submitEditClassified: 'submitEditClassified',
      submitNewClassified: 'submitNewClassified',
      validateClient: 'validateClient'
    }),
    ...mapMutations(CLASSIFIED_FORM_NS, {
      setShowFloatingSaveButton: SET_SHOW_FLOATING_SAVE_BUTTON,
      setInvalidTriggered: SET_INVALID_TRIGGERED
    }),
    async onInvalid(e) {
      if (!this.invalidTriggered) {
        this.setInvalidTriggered(true)

        await nextTick()

        const yOffset = -80 // 52 for header and 28 to show label
        const y =
          e.target.getBoundingClientRect().top + window.pageYOffset + yOffset

        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    },
    onBeforeUnload(e) {
      if (this.formChanged) {
        // this message will not be shown, we just need a string
        const confirmationMessage =
          'Are you sure you want to leave the page? Any changes will not be saved'
        ;(e || window.event).returnValue = confirmationMessage
        return confirmationMessage
      } else e = null // i.e; if form state change show warning box, else don't show it.
    },
    termsChanged(state) {
      this.fieldChanged({ field: { name: 'accepted_terms', value: state } })
      this.termsAccepted = state
    },
    async handleSubmitButtonClick(
      scrollOption: { scroll: boolean } = undefined
    ) {
      this.setInvalidTriggered(false)
      const clientValidationStatus = await this.validateClient()

      if (
        !(this.userAgentService.isFirefox() && !this.isPc) &&
        (!clientValidationStatus || this.$parent.$el.reportValidity() === false)
      ) {
        // mobile version of firefox not showing validation errors (it doesnt even scroll) https://bugzilla.mozilla.org/show_bug.cgi?id=1510450
        // client side navigation return an error
        return false
      }

      if (this.requiresTerms && !this.termsAccepted) {
        this.$snackbar.error(
          this.$i18n.t(
            'you must accept our terms and conditions to submit a classified'
          )
        )
        return false
      }

      if (!this.hasPrice && !this.isRental && !this.isJobs) {
        this.warningTitle = this.$t('missing price data') as string
        this.warningText = this.$t(
          this.editMode
            ? 'fill in the price in the offer details to make the classified available to users'
            : 'you have not set a price and your classified appears as unavailable'
        ) as string

        this.isWarningConfirmationVisible = true
      } else if (this.isCar && !this.model) {
        this.warningTitle = this.$t('no_model_title') as string
        this.warningText = this.$t('no_model_warning') as string
        this.isWarningConfirmationVisible = true
      } else {
        await this.handleSubmitData(scrollOption)
      }
    },

    async handleSubmitData(scrollOption: { scroll: boolean } = undefined) {
      if (this.editMode) {
        await this.submitEditClassified(scrollOption)
      } else {
        const tab = this.requiresPayment ? 'charges' : null
        await this.submitNewClassified(tab)
      }
    },
    submitButtonVisibilityChanged(isVisible: boolean) {
      this.setShowFloatingSaveButton(!isVisible)
    },
    async handleOk() {
      this.isWarningConfirmationVisible = false
      await this.handleSubmitData()
    },
    handleCancel() {
      let name = 'offer_info'

      if (!this.model && this.isCar) {
        name = 'vehicle_details'
      }
      const offerInfo = document.getElementsByName(name)[0]
      if (offerInfo) {
        offerInfo.scrollIntoView({ behavior: 'smooth' })
      }

      this.isWarningConfirmationVisible = false
    },
    handleFloatingSaveButtonClick() {
      this.handleSubmitButtonClick({ scroll: false })
    }
  }
}
